import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TablePagination,
  TableSortLabel,
  CircularProgress,
  Button,
  Stack,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  IconButton,
  Grid,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { useSnackbar } from 'notistack';
import cuentaCorrienteProveedoresService, {
  CuentaCorrienteProveedorSaldo,
  ProveedorDeudaPorVencimiento,
  type EstadisticasCuentaCorrienteProveedores,
  type SaldosProveedorSortBy,
  type SaldosProveedorSortDir,
} from '../../services/cuentaCorrienteProveedoresService';
import rutaService, { Ruta } from '../../services/rutaService';
import ramoProvService, { RamoProv } from '../../services/ramoProvService';
import { formatCurrency } from '../../utils/formatters';
import {
  FileDownload as FileDownloadIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Search as SearchIcon,
  ListAlt as ListAltIcon,
  ArrowBack as ArrowBackIcon,
  DateRange as DateRangeIcon,
  Paid as PaidIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  People as PeopleIcon,
  Schedule as ScheduleIcon,
  Payments as PaymentsIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import ImportarSaldosCtaCteDialog from '../../components/ImportarSaldosCtaCteDialog';
import * as XLSX from 'xlsx';
import { useNavigate } from 'react-router-dom';
import { navigateWithModuleBack } from '../../utils/moduleNavigationContext';
import PDFViewer from '../../components/shared/PDFViewer';
import { searchableSelectAutocompleteProps } from '../../components/SearchableSelect';
import { getDashboardResumen } from '../../services/dashboardService';

const CUENTAS_PAGAR_PATH = '/compras/informes/cuentas-pagar';
const RESUMEN_CUENTA_FROM_STATE = {
  from: CUENTAS_PAGAR_PATH,
  fromLabel: 'Cuentas por Pagar',
} as const;

const emptyEstadisticas: EstadisticasCuentaCorrienteProveedores = {
  totalProveedoresConCuentaCorriente: 0,
  saldoTotalGeneral: 0,
  saldoVencidoTotal: 0,
  totalMovimientos: 0,
  ultimaActualizacion: '',
};

type SortableColumn = SaldosProveedorSortBy;

const SORTABLE_COLUMNS: { id: SortableColumn; label: string; align?: 'left' | 'right' | 'center' }[] = [
  { id: 'nroProveedor', label: 'Nro. Proveedor' },
  { id: 'nombre', label: 'Nombre' },
  { id: 'telefono', label: 'Teléfono' },
  { id: 'saldoTotal', label: 'Saldo Total', align: 'right' },
  { id: 'saldoVigente', label: 'Saldo Vigente', align: 'right' },
  { id: 'saldoVencido', label: 'Saldo Vencido', align: 'right' },
];

async function buildListadoDeudaPdfBlob(
  rows: ProveedorDeudaPorVencimiento[],
  opts: { fechaHasta: string; ruta: Ruta | null; ramo: RamoProv | null }
): Promise<Blob> {
  const { jsPDF } = await import('jspdf');
  const autoTable = (await import('jspdf-autotable')).default;
  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
  doc.setLineHeightFactor(1);
  const marginX = 40;
  let y = 50;
  doc.setFontSize(18);
  doc.text('Deuda por vencimiento', marginX, y);
  y += 22;
  doc.setFontSize(12);
  const hastaLabel = new Date(`${opts.fechaHasta}T12:00:00`).toLocaleDateString('es-AR', { dateStyle: 'long' });
  doc.text(`Cuentas por pagar — vencimientos hasta ${hastaLabel}`, marginX, y);
  y += 18;
  const partes: string[] = [];
  if (opts.ruta) {
    partes.push(`Ruta: ${opts.ruta.nombreRuta ?? opts.ruta.codigoRuta ?? ''}`);
  }
  if (opts.ramo) {
    partes.push(
      `Ramo: ${(opts.ramo.nombreramoprov || '').trim() || (opts.ramo.codigoramoprov || '').trim()}`
    );
  }
  if (partes.length) {
    doc.text(partes.join('  ·  '), marginX, y);
    y += 16;
  }
  doc.setFontSize(10);
  doc.text(`Generado: ${new Date().toLocaleString('es-AR')}`, marginX, y);
  y += 20;

  const totalGeneral = rows.reduce(
    (sum, r) => sum + (Number(r.deudaHastaVencimiento) || 0),
    0
  );

  const cellPadBody = { top: 0, right: 2, bottom: 0, left: 2 };
  const cellPadHead = { top: 1, right: 2, bottom: 1, left: 2 };

  autoTable(doc, {
    startY: y,
    theme: 'striped',
    head: [['Nro.', 'Proveedor', 'Ruta', 'Ramo', 'Deuda']],
    body: rows.map((r) => [
      String(r.nroProveedor),
      r.nombre,
      (r.nombreRuta ?? r.codigoRuta ?? '—') as string,
      (r.nombreRamoProv ?? r.codigoRamoProv) as string,
      formatCurrency(r.deudaHastaVencimiento)
    ]),
    ...(rows.length
      ? {
          foot: [
            [
              {
                content: `Totales — ${rows.length} fila${rows.length === 1 ? '' : 's'}`,
                colSpan: 4,
                styles: { halign: 'right', fontStyle: 'bold' as const }
              },
              {
                content: formatCurrency(totalGeneral),
                styles: { halign: 'right' as const, fontStyle: 'bold' as const }
              }
            ]
          ],
          footStyles: {
            fillColor: [240, 245, 250] as [number, number, number],
            textColor: [0, 0, 0] as [number, number, number],
            fontSize: 7.5,
            cellPadding: { top: 2, right: 2, bottom: 2, left: 2 }
          },
          showFoot: 'lastPage' as const
        }
      : { showFoot: 'never' as const }),
    margin: { left: marginX, right: marginX },
    styles: {
      fontSize: 7,
      cellPadding: cellPadBody,
      overflow: 'linebreak',
      valign: 'top',
      minCellHeight: 0
    },
    headStyles: {
      fillColor: [25, 118, 210],
      textColor: 255,
      fontSize: 7.5,
      cellPadding: cellPadHead,
      lineWidth: 0,
      valign: 'middle'
    },
    bodyStyles: {
      valign: 'top'
    },
    columnStyles: {
      0: { cellWidth: 40 },
      1: { cellWidth: 128 },
      2: { cellWidth: 78 },
      3: { cellWidth: 78 },
      4: { halign: 'right' }
    }
  });

  const totalPaginas = doc.getNumberOfPages();
  const anchoPag = doc.internal.pageSize.getWidth();
  const altoPag = doc.internal.pageSize.getHeight();
  const yPie = altoPag - 18;
  for (let p = 1; p <= totalPaginas; p += 1) {
    doc.setPage(p);
    doc.setFontSize(8);
    doc.setTextColor(90, 90, 90);
    doc.text(`Página ${p} de ${totalPaginas}`, anchoPag / 2, yPie, { align: 'center' });
  }
  doc.setTextColor(0, 0, 0);

  return doc.output('blob');
}

/** Claves de listado disponibles (extensible). */
const LISTADOS = [
  {
    id: 'deudaVencida' as const,
    titulo: 'Deuda vencida a fecha',
    descripcion: 'Proveedores con saldo en cuenta corriente por vencimientos hasta una fecha, filtrable por ruta y ramo.'
  }
] as const;

const CuentasPorPagar: React.FC = () => {
  const navigate = useNavigate();

  const [saldos, setSaldos] = useState<CuentaCorrienteProveedorSaldo[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<SaldosProveedorSortBy>('nombre');
  const [sortDir, setSortDir] = useState<SaldosProveedorSortDir>('asc');
  const [soloConSaldo, setSoloConSaldo] = useState(false);
  const [soloConSaldoVencido, setSoloConSaldoVencido] = useState(false);

  const [openImportarSaldos, setOpenImportarSaldos] = useState(false);
  const [importReloadKey, setImportReloadKey] = useState(0);
  const [listadosOpen, setListadosOpen] = useState(false);
  /** null = modal de elección; 'deudaVencida' = formulario de ese listado */
  const [listadoEmitir, setListadoEmitir] = useState<null | 'deudaVencida'>(null);
  const [fechaHastaListado, setFechaHastaListado] = useState(() => new Date().toISOString().slice(0, 10));
  const [rutaFiltro, setRutaFiltro] = useState<Ruta | null>(null);
  const [ramoFiltro, setRamoFiltro] = useState<RamoProv | null>(null);
  const [rutasOptions, setRutasOptions] = useState<Ruta[]>([]);
  const [ramosOptions, setRamosOptions] = useState<RamoProv[]>([]);
  const [loadingCatalogosListado, setLoadingCatalogosListado] = useState(false);
  const [_listadoDeudaRows, setListadoDeudaRows] = useState<ProveedorDeudaPorVencimiento[]>([]);
  const [loadingListadoDeuda, setLoadingListadoDeuda] = useState(false);
  const [exportingListadoDeuda, setExportingListadoDeuda] = useState(false);
  const [listadoDeudaPdfUrl, setListadoDeudaPdfUrl] = useState<string | null>(null);
  const [estadisticas, setEstadisticas] =
    useState<EstadisticasCuentaCorrienteProveedores>(emptyEstadisticas);
  const [loadingKpis, setLoadingKpis] = useState(false);
  const [pagosMesActual, setPagosMesActual] = useState(0);
  const [diasPromedioPago, setDiasPromedioPago] = useState<number | null>(null);

  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const goToResumenCuenta = (nroProveedor: number) => {
    navigate(`/compras/informes/resumen-cuenta/${nroProveedor}`, {
      state: RESUMEN_CUENTA_FROM_STATE,
    });
  };

  useEffect(() => {
    if (!listadosOpen || listadoEmitir !== 'deudaVencida') return;
    let cancelled = false;
    const loadCatalogos = async () => {
      setLoadingCatalogosListado(true);
      try {
        const pageSize = 20;
        const rutas: Ruta[] = [];
        let p = 1;
        while (!cancelled) {
          const r = await rutaService.getRutasPaged({ pageNumber: p, pageSize });
          rutas.push(...r.items);
          if (p >= r.totalPages || r.items.length === 0) break;
          p += 1;
        }
        const ramos: RamoProv[] = [];
        p = 1;
        while (!cancelled) {
          const r = await ramoProvService.getRamosProvPaged({ pageNumber: p, pageSize });
          ramos.push(...r.items);
          if (p >= r.totalPages || r.items.length === 0) break;
          p += 1;
        }
        if (!cancelled) {
          setRutasOptions(rutas);
          setRamosOptions(ramos);
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          enqueueSnackbar('No se pudieron cargar rutas o ramos para el listado.', { variant: 'error' });
        }
      } finally {
        if (!cancelled) setLoadingCatalogosListado(false);
      }
    };
    void loadCatalogos();
    return () => {
      cancelled = true;
    };
  }, [listadosOpen, listadoEmitir, enqueueSnackbar]);

  const handleCloseListadoDeudaPdf = () => {
    setListadoDeudaPdfUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };

  const handleGenerarListadoDeuda = async () => {
    if (!fechaHastaListado) {
      enqueueSnackbar('Indicá la fecha hasta de vencimiento.', { variant: 'warning' });
      return;
    }
    try {
      setLoadingListadoDeuda(true);
      const rows = await cuentaCorrienteProveedoresService.getDeudaPorVencimiento({
        fechaHasta: fechaHastaListado,
        codigoRuta: rutaFiltro?.codigoRuta ?? undefined,
        codigoRamoProv: ramoFiltro?.codigoramoprov
      });
      setListadoDeudaRows(rows);
      if (!rows.length) {
        enqueueSnackbar('No hay proveedores con deuda en ese corte.', { variant: 'warning' });
        return;
      }
      const blob = await buildListadoDeudaPdfBlob(rows, {
        fechaHasta: fechaHastaListado,
        ruta: rutaFiltro,
        ramo: ramoFiltro
      });
      setListadoDeudaPdfUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(blob);
      });
      setListadoEmitir(null);
      setListadosOpen(false);
      enqueueSnackbar(`PDF generado: ${rows.length} proveedor(es).`, { variant: 'success' });
    } catch (error) {
      console.error(error);
      enqueueSnackbar('No se pudo generar el listado de deuda por vencimiento.', { variant: 'error' });
    } finally {
      setLoadingListadoDeuda(false);
    }
  };

  const writeListadoDeudaToExcel = (rows: ProveedorDeudaPorVencimiento[], fechaCorte: string) => {
    const data: Array<Record<string, string | number>> = rows.map((row) => ({
      'Nro. Proveedor': row.nroProveedor,
      Nombre: row.nombre,
      'Cód. Ruta': row.codigoRuta ?? '',
      Ruta: row.nombreRuta ?? '',
      'Cód. Ramo': row.codigoRamoProv,
      Ramo: row.nombreRamoProv ?? '',
      'Deuda venc. hasta fecha': row.deudaHastaVencimiento
    }));
    if (rows.length) {
      const totalGeneral = rows.reduce(
        (sum, r) => sum + (Number(r.deudaHastaVencimiento) || 0),
        0
      );
      data.push({
        'Nro. Proveedor': '',
        Nombre: `Total de filas: ${rows.length}`,
        'Cód. Ruta': '',
        Ruta: '',
        'Cód. Ramo': '',
        Ramo: '',
        'Deuda venc. hasta fecha': ''
      });
      data.push({
        'Nro. Proveedor': '',
        Nombre: 'Total general',
        'Cód. Ruta': '',
        Ruta: '',
        'Cód. Ramo': '',
        Ramo: '',
        'Deuda venc. hasta fecha': totalGeneral
      });
    }
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'DeudaPorVencimiento');
    const slug = fechaCorte.replace(/-/g, '');
    XLSX.writeFile(workbook, `deuda_proveedores_por_vencimiento_${slug}.xlsx`);
  };

  const handleExportListadoDeudaExcel = async () => {
    if (!fechaHastaListado) {
      enqueueSnackbar('Indicá la fecha hasta de vencimiento para exportar.', { variant: 'warning' });
      return;
    }
    try {
      setExportingListadoDeuda(true);
      const rows = await cuentaCorrienteProveedoresService.getDeudaPorVencimiento({
        fechaHasta: fechaHastaListado,
        codigoRuta: rutaFiltro?.codigoRuta ?? undefined,
        codigoRamoProv: ramoFiltro?.codigoramoprov
      });
      if (!rows.length) {
        enqueueSnackbar('No hay proveedores con deuda en ese corte para exportar.', { variant: 'warning' });
        return;
      }
      setListadoDeudaRows(rows);
      writeListadoDeudaToExcel(rows, fechaHastaListado);
      enqueueSnackbar('Exportación completada.', { variant: 'success' });
    } catch (error) {
      console.error(error);
      enqueueSnackbar('No se pudo exportar el listado a Excel.', { variant: 'error' });
    } finally {
      setExportingListadoDeuda(false);
    }
  };

  const handleCloseListados = () => {
    setListadoEmitir(null);
    setListadosOpen(false);
  };

  const handleOpenListados = () => {
    setListadoEmitir(null);
    setListadoDeudaPdfUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setListadosOpen(true);
  };

  // Debounce para la busqueda y carga de datos
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const params: {
          pageNumber: number;
          pageSize: number;
          searchTerm?: string;
          sortBy: SaldosProveedorSortBy;
          sortDir: SaldosProveedorSortDir;
          soloConSaldo?: boolean;
          soloConSaldoVencido?: boolean;
        } = {
          pageNumber: page + 1,
          pageSize: rowsPerPage,
          sortBy,
          sortDir,
        };

        if (searchTerm.trim()) {
          params.searchTerm = searchTerm.trim();
        }
        if (soloConSaldo) {
          params.soloConSaldo = true;
        }
        if (soloConSaldoVencido) {
          params.soloConSaldoVencido = true;
        }

        const response = await cuentaCorrienteProveedoresService.getSaldos(params);

        setSaldos(response.items);
        setTotalCount(response.totalCount);
      } catch (error) {
        console.error('Error al obtener las cuentas por pagar:', error);
        enqueueSnackbar('No se pudieron cargar los saldos de proveedores.', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };

    const timer = setTimeout(() => {
      loadData();
    }, searchTerm.trim() ? 500 : 0);

    return () => clearTimeout(timer);
  }, [searchTerm, page, rowsPerPage, sortBy, sortDir, soloConSaldo, soloConSaldoVencido, importReloadKey, enqueueSnackbar]);

  useEffect(() => {
    let cancelled = false;
    const loadKpis = async () => {
      setLoadingKpis(true);
      try {
        const [stats, resumen] = await Promise.all([
          cuentaCorrienteProveedoresService.getEstadisticas(),
          getDashboardResumen(),
        ]);
        if (cancelled) return;
        setEstadisticas(stats);
        setPagosMesActual(Number(resumen.totalPagadoMes) || 0);
        setDiasPromedioPago(
          resumen.diasPromedioPagoCompras != null
            ? Number(resumen.diasPromedioPagoCompras)
            : null
        );
      } catch (error) {
        console.error(error);
        if (!cancelled) {
          enqueueSnackbar('No se pudieron cargar los indicadores de cuentas por pagar.', {
            variant: 'warning',
          });
        }
      } finally {
        if (!cancelled) setLoadingKpis(false);
      }
    };
    void loadKpis();
    return () => {
      cancelled = true;
    };
  }, [importReloadKey, enqueueSnackbar]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleRequestSort = (column: SortableColumn) => {
    if (sortBy === column) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortDir('asc');
    }
    setPage(0);
  };

  const handleToggleSoloConSaldo = () => {
    setSoloConSaldo((prev) => !prev);
    setPage(0);
  };

  const handleToggleSoloConSaldoVencido = () => {
    setSoloConSaldoVencido((prev) => !prev);
    setPage(0);
  };

  const getAllSaldos = async (): Promise<CuentaCorrienteProveedorSaldo[]> => {
    const allItems: CuentaCorrienteProveedorSaldo[] = [];
    const pageSize = 50;
    let pageNumber = 1;
    let hasMore = true;

    const baseParams: {
      pageSize: number;
      searchTerm?: string;
      sortBy: SaldosProveedorSortBy;
      sortDir: SaldosProveedorSortDir;
      soloConSaldo?: boolean;
      soloConSaldoVencido?: boolean;
    } = {
      pageSize,
      sortBy,
      sortDir,
    };

    if (searchTerm.trim()) {
      baseParams.searchTerm = searchTerm.trim();
    }
    if (soloConSaldo) {
      baseParams.soloConSaldo = true;
    }
    if (soloConSaldoVencido) {
      baseParams.soloConSaldoVencido = true;
    }

    while (hasMore) {
      const response = await cuentaCorrienteProveedoresService.getSaldos({
        ...baseParams,
        pageNumber
      });

      allItems.push(...response.items);

      const totalPages = Math.ceil(response.totalCount / pageSize);
      hasMore = pageNumber < totalPages;
      pageNumber++;
    }

    return allItems;
  };

  const handleExportExcel = async () => {
    if (totalCount === 0) {
      enqueueSnackbar('No hay datos para exportar.', { variant: 'warning' });
      return;
    }

    try {
      setExporting(true);
      const allItems = await getAllSaldos();

      if (!allItems.length) {
        enqueueSnackbar('No hay datos para exportar.', { variant: 'warning' });
        return;
      }

      const data = allItems.map((item) => ({
        'Nro. Proveedor': item.nroProveedor,
        Nombre: item.nombre,
        'Saldo Total': item.saldoTotal,
        'Saldo Vigente': item.saldoVigente,
        'Saldo Vencido': item.saldoVencido
      }));

      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'CuentasPorPagar');

      const today = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(workbook, `cuentas_por_pagar_${today}.xlsx`);
      enqueueSnackbar('Exportación completada.', { variant: 'success' });
    } catch (error) {
      console.error('Error al exportar a Excel:', error);
      enqueueSnackbar('No se pudo exportar a Excel.', { variant: 'error' });
    } finally {
      setExporting(false);
    }
  };

  const handleShare = async () => {
    if (totalCount === 0) {
      enqueueSnackbar('No hay información para compartir.', { variant: 'warning' });
      return;
    }

    try {
      setExportingPdf(true);
      const allItems = await getAllSaldos();

      if (!allItems.length) {
        enqueueSnackbar('No hay información para compartir.', { variant: 'warning' });
        return;
      }

      const { jsPDF } = await import('jspdf');

      const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
      const marginX = 36;
      let currentY = 44;

      doc.setFontSize(16);
      doc.text('Cuentas por Pagar', marginX, currentY);
      currentY += 18;

      doc.setFontSize(9);
      const today = new Date();
      doc.text(`Generado: ${today.toLocaleString('es-AR')}`, marginX, currentY);
      currentY += 14;

      const totalSaldo = allItems.reduce((s, r) => s + (Number(r.saldoTotal) || 0), 0);
      const totalVigente = allItems.reduce((s, r) => s + (Number(r.saldoVigente) || 0), 0);
      const totalVencido = allItems.reduce((s, r) => s + (Number(r.saldoVencido) || 0), 0);

      const kpiDeuda =
        estadisticas.saldoTotalGeneral != null
          ? estadisticas.saldoTotalGeneral
          : totalSaldo;
      const kpiProveedores =
        estadisticas.totalProveedoresConCuentaCorriente > 0
          ? estadisticas.totalProveedoresConCuentaCorriente
          : allItems.length;
      const kpiDias =
        diasPromedioPago != null ? `${diasPromedioPago.toFixed(1)} días` : '—';

      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.text('Resumen', marginX, currentY);
      currentY += 11;
      doc.setFont('helvetica', 'normal');
      const kpiLineas = [
        `Deuda total: ${formatCurrency(kpiDeuda)}`,
        `Prom. días de pago (mes): ${kpiDias}`,
        `Proveedores con deuda: ${kpiProveedores.toLocaleString('es-AR')}`,
        `Pagos del mes actual: ${formatCurrency(pagosMesActual)}`,
      ];
      for (const linea of kpiLineas) {
        doc.text(linea, marginX, currentY);
        currentY += 10;
      }
      currentY += 6;

      const autoTable = (await import('jspdf-autotable')).default;
      const bodyFontSize = 7;
      const cellPad = { top: 1.5, right: 2, bottom: 1.5, left: 2 };

      autoTable(doc, {
        startY: currentY,
        theme: 'striped',
        head: [['Nro.', 'Proveedor', 'Teléfono', 'Saldo Total', 'Vigente', 'Vencido']],
        body: allItems.map((row) => [
          String(row.nroProveedor),
          row.nombre,
          row.telefono?.trim() || '—',
          formatCurrency(row.saldoTotal),
          formatCurrency(row.saldoVigente),
          formatCurrency(row.saldoVencido),
        ]),
        foot: [
          [
            {
              content: `Totales — ${allItems.length} fila${allItems.length === 1 ? '' : 's'}`,
              colSpan: 3,
              styles: { halign: 'right', fontStyle: 'bold' as const },
            },
            {
              content: formatCurrency(totalSaldo),
              styles: { halign: 'right' as const, fontStyle: 'bold' as const },
            },
            {
              content: formatCurrency(totalVigente),
              styles: { halign: 'right' as const, fontStyle: 'bold' as const },
            },
            {
              content: formatCurrency(totalVencido),
              styles: { halign: 'right' as const, fontStyle: 'bold' as const },
            },
          ],
        ],
        showFoot: 'lastPage',
        margin: { left: marginX, right: marginX },
        styles: {
          fontSize: bodyFontSize,
          cellPadding: cellPad,
          overflow: 'linebreak',
          valign: 'middle',
          minCellHeight: bodyFontSize + 2,
        },
        headStyles: {
          fillColor: [25, 118, 210],
          textColor: 255,
          fontSize: 7,
          cellPadding: cellPad,
          valign: 'middle',
        },
        footStyles: {
          fillColor: [240, 245, 250],
          textColor: [0, 0, 0],
          fontSize: 7,
          cellPadding: { top: 2, right: 2, bottom: 2, left: 2 },
        },
        columnStyles: {
          0: { cellWidth: 36 },
          1: { cellWidth: 180 },
          2: { cellWidth: 72 },
          3: { halign: 'right', cellWidth: 78 },
          4: { halign: 'right', cellWidth: 70 },
          5: { halign: 'right', cellWidth: 70 },
        },
      });

      const totalPaginas = doc.getNumberOfPages();
      const anchoPag = doc.internal.pageSize.getWidth();
      const altoPag = doc.internal.pageSize.getHeight();
      for (let p = 1; p <= totalPaginas; p += 1) {
        doc.setPage(p);
        doc.setFontSize(8);
        doc.setTextColor(90, 90, 90);
        doc.text(`Página ${p} de ${totalPaginas}`, anchoPag / 2, altoPag - 18, { align: 'center' });
      }
      doc.setTextColor(0, 0, 0);

      const pdfBlob = doc.output('blob');
      const fileName = `cuentas_por_pagar_${today.toISOString().slice(0, 10)}.pdf`;

      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.target = '_self';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      enqueueSnackbar('PDF descargado correctamente.', { variant: 'success' });
    } catch (error: unknown) {
      const err = error as { name?: string };
      if (err?.name === 'AbortError') {
        enqueueSnackbar('Compartir cancelado por el usuario.', { variant: 'warning' });
      } else {
        console.error('Error al generar el PDF para compartir:', error);
        enqueueSnackbar('No se pudo generar el PDF para compartir.', { variant: 'error' });
      }
    } finally {
      setExportingPdf(false);
    }
  };

  const renderPagarButton = (row: CuentaCorrienteProveedorSaldo) => (
    <IconButton
      size="small"
      color="primary"
      title="Pagar"
      aria-label="Pagar"
      onClick={() => {
        navigateWithModuleBack(
          navigate,
          `/tesoreria/orden-pago/nuevo?nroProveedor=${row.nroProveedor}`,
          '/compras',
        );
      }}
      sx={{
        bgcolor: 'primary.main',
        color: 'common.white',
        '&:hover': {
          bgcolor: 'primary.dark'
        }
      }}
    >
      <PaidIcon fontSize="small" />
    </IconButton>
  );

  const renderBody = () => {
    if (loading) {
      return (
        <TableRow>
          <TableCell colSpan={7} align="center">
            <CircularProgress size={24} />
          </TableCell>
        </TableRow>
      );
    }

    if (!loading && saldos.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={7} align="center">
            {searchTerm.trim() ? 'No se encontraron resultados para la búsqueda.' : 'No hay proveedores con saldo.'}
          </TableCell>
        </TableRow>
      );
    }

    return saldos.map((row) => (
      <TableRow
        key={row.nroProveedor}
        onClick={() => {
          goToResumenCuenta(row.nroProveedor);
        }}
        sx={{
          cursor: 'pointer',
          '&:hover': {
            bgcolor: '#f9f9f9'
          }
        }}
      >
        <TableCell sx={{ fontWeight: 500 }}>{row.nroProveedor}</TableCell>
        <TableCell>{row.nombre}</TableCell>
        <TableCell>{row.telefono || '-'}</TableCell>
        <TableCell align="right" sx={{ fontWeight: 600, color: '#1976d2' }}>
          {formatCurrency(row.saldoTotal)}
        </TableCell>
        <TableCell align="right">{formatCurrency(row.saldoVigente)}</TableCell>
        <TableCell
          align="right"
          sx={{
            color: row.saldoVencido > 0 ? '#f44336' : 'inherit',
            fontWeight: row.saldoVencido > 0 ? 600 : 'normal'
          }}
        >
          {formatCurrency(row.saldoVencido)}
        </TableCell>
        <TableCell align="center" onClick={(e) => e.stopPropagation()} sx={{ width: '1%', whiteSpace: 'nowrap' }}>
          {renderPagarButton(row)}
        </TableCell>
      </TableRow>
    ));
  };

  const renderCardCampo = (label: string, valor: React.ReactNode, sx?: object) => (
    <Box sx={sx}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
        {label}
      </Typography>
      <Typography variant="body2">{valor}</Typography>
    </Box>
  );

  const renderCardsMobile = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      );
    }

    if (!loading && saldos.length === 0) {
      return (
        <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
          {searchTerm.trim() ? 'No se encontraron resultados para la búsqueda.' : 'No hay proveedores con saldo.'}
        </Typography>
      );
    }

    return (
      <Stack spacing={2}>
        {saldos.map((row) => (
          <Paper
            key={row.nroProveedor}
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2,
              cursor: 'pointer',
              '&:hover': { boxShadow: '0 4px 8px rgba(0,0,0,0.1)' },
            }}
            onClick={() => goToResumenCuenta(row.nroProveedor)}
          >
            <Stack spacing={1.5}>
              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  {renderCardCampo('Nro. Proveedor', row.nroProveedor)}
                </Grid>
                <Grid item xs={6}>
                  {renderCardCampo('Nombre', row.nombre)}
                </Grid>
                <Grid item xs={6}>
                  {renderCardCampo('Teléfono', row.telefono || '-')}
                </Grid>
                <Grid item xs={6}>
                  {renderCardCampo(
                    'Saldo Total',
                    <Box component="span" sx={{ fontWeight: 600, color: '#1976d2' }}>
                      {formatCurrency(row.saldoTotal)}
                    </Box>
                  )}
                </Grid>
                <Grid item xs={6}>
                  {renderCardCampo('Saldo Vigente', formatCurrency(row.saldoVigente))}
                </Grid>
                <Grid item xs={6}>
                  {renderCardCampo(
                    'Saldo Vencido',
                    <Box
                      component="span"
                      sx={{
                        color: row.saldoVencido > 0 ? '#f44336' : 'inherit',
                        fontWeight: row.saldoVencido > 0 ? 600 : 'normal',
                      }}
                    >
                      {formatCurrency(row.saldoVencido)}
                    </Box>
                  )}
                </Grid>
              </Grid>
              <Box onClick={(e) => e.stopPropagation()}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                  Acciones
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<PaidIcon />}
                  onClick={() =>
                    navigateWithModuleBack(
                      navigate,
                      `/tesoreria/orden-pago/nuevo?nroProveedor=${row.nroProveedor}`,
                      '/compras',
                    )
                  }
                  sx={{ textTransform: 'none', borderRadius: 2 }}
                >
                  Pagar
                </Button>
              </Box>
            </Stack>
          </Paper>
        ))}
      </Stack>
    );
  };

  const paginationProps = {
    rowsPerPageOptions: [10, 20, 50, 100] as number[],
    component: 'div' as const,
    count: totalCount,
    rowsPerPage,
    page,
    onPageChange: handleChangePage,
    onRowsPerPageChange: handleChangeRowsPerPage,
    labelRowsPerPage: 'Filas por página',
    labelDisplayedRows: ({ from, to, count }: { from: number; to: number; count: number }) => {
      if (searchTerm.trim() || soloConSaldo || soloConSaldoVencido) {
        return `${from}-${to} de ${count} (filtrado)`;
      }
      return `${from}-${to} de ${count}`;
    },
  };

  const renderResumenTab = () => (
    <>
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Buscar por número de proveedor o nombre..."
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              bgcolor: '#fafafa',
              '&:hover': {
                bgcolor: '#f5f5f5'
              },
              '&.Mui-focused': {
                bgcolor: '#ffffff'
              }
            }
          }}
        />
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
          <Chip
            label="Con saldo"
            clickable
            color={soloConSaldo ? 'primary' : 'default'}
            variant={soloConSaldo ? 'filled' : 'outlined'}
            onClick={handleToggleSoloConSaldo}
          />
          <Chip
            label="Con saldo vencido"
            clickable
            color={soloConSaldoVencido ? 'primary' : 'default'}
            variant={soloConSaldoVencido ? 'filled' : 'outlined'}
            onClick={handleToggleSoloConSaldoVencido}
          />
        </Stack>
      </Box>

      {isMobile ? (
        <Box>
          {renderCardsMobile()}
          <TablePagination
            {...paginationProps}
            sx={{
              mt: 2,
              '& .MuiTablePagination-toolbar': {
                flexWrap: 'wrap',
                gap: 1,
                px: 1
              }
            }}
          />
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: 2,
            boxShadow: 'none',
            border: '1px solid #e0e0e0'
          }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                {SORTABLE_COLUMNS.map((col) => (
                  <TableCell
                    key={col.id}
                    align={col.align}
                    sortDirection={sortBy === col.id ? sortDir : false}
                    sx={{ fontWeight: 600 }}
                  >
                    <TableSortLabel
                      active={sortBy === col.id}
                      direction={sortBy === col.id ? sortDir : 'asc'}
                      onClick={() => handleRequestSort(col.id)}
                    >
                      {col.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
                <TableCell sx={{ fontWeight: 600 }} align="center">
                  Acciones
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>{renderBody()}</TableBody>
          </Table>
          <TablePagination
            {...paginationProps}
            sx={{
              borderTop: '1px solid #e0e0e0'
            }}
          />
        </TableContainer>
      )}
    </>
  );

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card
        sx={{
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            sx={{ mb: 2 }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/compras/informes')}
                sx={{ textTransform: 'none' }}
              >
                Informes
              </Button>
              <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
                Cuentas por Pagar
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<CloudUploadIcon />}
                onClick={() => setOpenImportarSaldos(true)}
                disabled={loading}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 500
                }}
              >
                Importar saldos
              </Button>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<ListAltIcon />}
                onClick={handleOpenListados}
                disabled={loading}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 500
                }}
              >
                Listados
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<FileDownloadIcon />}
                onClick={handleExportExcel}
                disabled={loading || exporting}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 500,
                  boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(25, 118, 210, 0.4)'
                  }
                }}
              >
                Exportar Excel
              </Button>
              <Button
                variant="outlined"
                startIcon={<PictureAsPdfIcon />}
                onClick={handleShare}
                disabled={loading || exportingPdf}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 500
                }}
              >
                Exportar PDF
              </Button>
            </Stack>
          </Stack>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: '#0097a7', color: '#fff', textAlign: 'center', py: { xs: 1.5, sm: 2 } }}>
                <AccountBalanceWalletIcon sx={{ fontSize: { xs: 28, sm: 40 }, mb: 0.5 }} />
                <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{ fontWeight: 600 }}>
                  {loadingKpis ? '…' : formatCurrency(estadisticas.saldoTotalGeneral)}
                </Typography>
                <Typography variant="caption">Deuda total</Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: '#d32f2f', color: '#fff', textAlign: 'center', py: { xs: 1.5, sm: 2 } }}>
                <ScheduleIcon sx={{ fontSize: { xs: 28, sm: 40 }, mb: 0.5 }} />
                <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{ fontWeight: 600 }}>
                  {loadingKpis
                    ? '…'
                    : diasPromedioPago != null
                      ? `${diasPromedioPago.toFixed(1)} días`
                      : '—'}
                </Typography>
                <Typography variant="caption">Prom. días de pago (mes)</Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: '#9c27b0', color: '#fff', textAlign: 'center', py: { xs: 1.5, sm: 2 } }}>
                <PeopleIcon sx={{ fontSize: { xs: 28, sm: 40 }, mb: 0.5 }} />
                <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{ fontWeight: 600 }}>
                  {loadingKpis
                    ? '…'
                    : estadisticas.totalProveedoresConCuentaCorriente.toLocaleString('es-AR')}
                </Typography>
                <Typography variant="caption">Proveedores con deuda</Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: '#ed6c02', color: '#fff', textAlign: 'center', py: { xs: 1.5, sm: 2 } }}>
                <PaymentsIcon sx={{ fontSize: { xs: 28, sm: 40 }, mb: 0.5 }} />
                <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{ fontWeight: 600 }}>
                  {loadingKpis ? '…' : formatCurrency(pagosMesActual)}
                </Typography>
                <Typography variant="caption">Pagos del mes actual</Typography>
              </Card>
            </Grid>
          </Grid>

          {renderResumenTab()}
        </CardContent>
      </Card>

      <Dialog
        open={listadosOpen}
        onClose={handleCloseListados}
        maxWidth={listadoEmitir === 'deudaVencida' ? 'sm' : 'md'}
        fullWidth
        fullScreen={isMobile}
      >
        {listadoEmitir == null ? (
          <>
            <DialogTitle>Elegir listado</DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Seleccioná qué listado emitir. Luego se abrirá la pantalla con filtros y generación.
              </Typography>
              <Stack spacing={1.5}>
                {LISTADOS.map((opt) => (
                  <Button
                    key={opt.id}
                    fullWidth
                    variant="outlined"
                    onClick={() => setListadoEmitir(opt.id)}
                    startIcon={<DateRangeIcon />}
                    sx={{
                      py: 1.5,
                      textTransform: 'none',
                      justifyContent: 'flex-start',
                      textAlign: 'left',
                      borderRadius: 2,
                      borderColor: '#e0e0e0',
                      '&:hover': { borderColor: '#1976d2', bgcolor: 'rgba(25, 118, 210, 0.04)' }
                    }}
                  >
                    <Box sx={{ display: 'block', textAlign: 'left' }}>
                      <Typography component="span" display="block" fontWeight={600} color="text.primary">
                        {opt.titulo}
                      </Typography>
                      <Typography component="span" variant="body2" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        {opt.descripcion}
                      </Typography>
                    </Box>
                  </Button>
                ))}
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={handleCloseListados} sx={{ textTransform: 'none' }}>
                Cerrar
              </Button>
            </DialogActions>
          </>
        ) : listadoEmitir === 'deudaVencida' ? (
          <>
            <DialogTitle
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                pr: 1
              }}
            >
              <IconButton
                size="small"
                onClick={() => setListadoEmitir(null)}
                aria-label="Volver a listados"
                edge="start"
              >
                <ArrowBackIcon />
              </IconButton>
              <Box component="span" sx={{ flex: 1 }}>
                Deuda vencida a fecha
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Proveedores filtrados por ruta y/o ramo. La deuda es la suma de saldos en cuenta corriente con fecha de
                vencimiento hasta la fecha indicada (solo comprobantes con vencimiento informado). No se listan
                proveedores con deuda 0. Al imprimir, se abre el PDF en el visor integrado; podés volver a abrir
                Listados.
              </Typography>
              <Stack spacing={2} sx={{ mb: 2 }}>
                <TextField
                  label="Fecha hasta (vencimiento)"
                  type="date"
                  value={fechaHastaListado}
                  onChange={(e) => setFechaHastaListado(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  size="small"
                />
                <Autocomplete
                  {...searchableSelectAutocompleteProps}
                  options={rutasOptions}
                  value={rutaFiltro}
                  onChange={(_, v) => setRutaFiltro(v)}
                  getOptionLabel={(o) => (o?.nombreRuta != null ? `${o.codigoRuta ?? ''} — ${o.nombreRuta}` : '')}
                  isOptionEqualToValue={(a, b) => (a?.codigoRuta ?? null) === (b?.codigoRuta ?? null)}
                  renderInput={(params) => (
                    <TextField {...params} label="Ruta (zona)" placeholder="Todas" size="small" />
                  )}
                  disabled={loadingCatalogosListado}
                />
                <Autocomplete
                  {...searchableSelectAutocompleteProps}
                  options={ramosOptions}
                  value={ramoFiltro}
                  onChange={(_, v) => setRamoFiltro(v)}
                  getOptionLabel={(o) =>
                    o?.codigoramoprov != null
                      ? `${(o.codigoramoprov || '').trim()} — ${o.nombreramoprov ?? ''}`
                      : ''
                  }
                  isOptionEqualToValue={(a, b) =>
                    (a?.codigoramoprov?.trim() ?? '') === (b?.codigoramoprov?.trim() ?? '')
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Ramo de proveedor" placeholder="Todos" size="small" />
                  )}
                  disabled={loadingCatalogosListado}
                />
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Button
                    variant="contained"
                    onClick={() => void handleGenerarListadoDeuda()}
                    disabled={loadingListadoDeuda || loadingCatalogosListado || exportingListadoDeuda}
                    sx={{ textTransform: 'none' }}
                  >
                    Imprimir
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<FileDownloadIcon />}
                    onClick={() => void handleExportListadoDeudaExcel()}
                    disabled={exportingListadoDeuda || loadingListadoDeuda || loadingCatalogosListado}
                    sx={{ textTransform: 'none' }}
                  >
                    Exportar Excel
                  </Button>
                  {(loadingCatalogosListado || loadingListadoDeuda) && <CircularProgress size={22} />}
                </Stack>
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={handleCloseListados} sx={{ textTransform: 'none' }}>
                Cerrar
              </Button>
            </DialogActions>
          </>
        ) : null}
      </Dialog>

      {listadoDeudaPdfUrl ? (
        <PDFViewer
          pdfUrl={listadoDeudaPdfUrl}
          onClose={handleCloseListadoDeudaPdf}
          title="Deuda por vencimiento — Cuentas por pagar"
          documentId="deuda-por-vencimiento"
          documentType="informeCliente"
          downloadFileName={`deuda_por_vencimiento_${fechaHastaListado.replace(/-/g, '')}.pdf`}
        />
      ) : null}

      <ImportarSaldosCtaCteDialog
        open={openImportarSaldos}
        onClose={() => setOpenImportarSaldos(false)}
        modo="proveedores"
        onImportado={() => {
          setPage(0);
          setImportReloadKey((k) => k + 1);
        }}
      />

    </Box>
  );
};

export default CuentasPorPagar;
